import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  BookOpenIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export type NutritionSectionId = 'overview' | 'records' | 'plans' | 'reference' | 'reports';
export type NutritionRecordsView = 'inventory' | 'mixes' | 'usage';
export type NutritionPlansView = 'templates' | 'optimizer' | 'goals';
export type NutritionReferenceView = 'requirements' | 'guide';
export type NutritionReportsView = 'nutritional' | 'cost' | 'inventory';

export interface NutritionSectionView {
  id: string;
  name: string;
}

export interface NutritionSection {
  id: NutritionSectionId;
  name: string;
  icon: typeof ChartBarIcon;
  description: string;
  views?: NutritionSectionView[];
}

export const NUTRITION_SECTIONS: NutritionSection[] = [
  {
    id: 'overview',
    name: 'Overview',
    icon: ChartBarIcon,
    description: 'Flock feeding snapshot, alerts, and quantity estimates',
  },
  {
    id: 'records',
    name: 'Records',
    icon: ClipboardDocumentListIcon,
    description: 'Feed inventory, daily mixes, and usage',
    views: [
      { id: 'inventory', name: 'Feed inventory' },
      { id: 'mixes', name: 'Daily mixes' },
      { id: 'usage', name: 'Usage & stock' },
    ],
  },
  {
    id: 'plans',
    name: 'Plans',
    icon: CogIcon,
    description: 'Templates, optimizer, and nutritional goals',
    views: [
      { id: 'templates', name: 'Mix templates' },
      { id: 'optimizer', name: 'Optimizer' },
      { id: 'goals', name: 'Goals' },
    ],
  },
  {
    id: 'reference',
    name: 'Reference',
    icon: BookOpenIcon,
    description: 'Nutritional requirements and user guide',
    views: [
      { id: 'requirements', name: 'Requirements' },
      { id: 'guide', name: 'User guide' },
    ],
  },
  {
    id: 'reports',
    name: 'Reports',
    icon: DocumentTextIcon,
    description: 'Nutritional, cost, and inventory analytics',
    views: [
      { id: 'nutritional', name: 'Nutritional reports' },
      { id: 'cost', name: 'Cost analysis' },
      { id: 'inventory', name: 'Inventory analytics' },
    ],
  },
];

export function parseNutritionSearchParams(search: URLSearchParams) {
  const section = (search.get('section') as NutritionSectionId) || 'overview';
  const view = search.get('view') || '';
  const legacyMap: Record<string, { section: NutritionSectionId; view: string }> = {
    inventory: { section: 'records', view: 'inventory' },
    mixes: { section: 'records', view: 'mixes' },
    feed: { section: 'records', view: 'inventory' },
  };
  if (legacyMap[view]) {
    return { section: legacyMap[view].section, view: legacyMap[view].view };
  }
  const validSection = NUTRITION_SECTIONS.some((s) => s.id === section) ? section : 'overview';
  const sectionDef = NUTRITION_SECTIONS.find((s) => s.id === validSection);
  const defaultView = sectionDef?.views?.[0]?.id || '';
  const validView = sectionDef?.views?.some((v) => v.id === view) ? view : defaultView;
  return { section: validSection, view: validView };
}

export function buildNutritionSearchParams(section: NutritionSectionId, view?: string): string {
  const params = new URLSearchParams();
  if (section !== 'overview') params.set('section', section);
  if (view) params.set('view', view);
  const query = params.toString();
  return query ? `?${query}` : '';
}
